import React, { useEffect } from "react";
import { Position, useUpdateNodeInternals } from "@xyflow/react";
import { GripIcon, X } from "lucide-react";
import type { NodeProps, HandleProps } from "./types";
import { useFlow } from "../../lib/store";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { CustomHandle } from "./CustomHandle";

const NodeHandles = ({
  handles = [],
}: {
  handles: BaseNodeProps["handles"];
}) => {
  const leftHandles = handles.filter((h) => h.position === Position.Left);
  const rightHandles = handles.filter((h) => h.position === Position.Right);

  return (
    <>
      {leftHandles.map((handle, index) => {
        const topPercentage = `${
          (100 / (leftHandles.length + 1)) * (index + 1)
        }%`;
        return (
          <CustomHandle
            key={`${handle.id}-${index}-${handle.label}-${handle.position}`}
            type={handle.type}
            position={Position.Left}
            id={handle.id}
            label={handle.label}
            style={{ top: topPercentage }}
          />
        );
      })}
      {rightHandles.map((handle, index) => {
        const topPercentage = `${
          (100 / (rightHandles.length + 1)) * (index + 1)
        }%`;
        return (
          <CustomHandle
            key={`${handle.id}-${index}-${handle.label}-${handle.position}`}
            type={handle.type}
            position={Position.Right}
            id={handle.id}
            label={handle.label}
            style={{ top: topPercentage }}
          />
        );
      })}
    </>
  );
};

interface BaseNodeProps extends NodeProps {
  children: React.ReactNode;
  title?: string;
  icon?: React.ReactElement;
  handles?: HandleProps[];
  id: string;
  className?: string;
}

const BaseNode: React.FC<BaseNodeProps> = ({
  children,
  title,
  icon,
  handles = [],
  id,
  className,
}) => {
  const { removeNode } = useFlow();

  const handleRemove = () => {
    removeNode(id);
  };

  const updateNodeInternals = useUpdateNodeInternals();
  useEffect(() => {
    updateNodeInternals(id);
  }, [handles]);
  return (
    <div
      className={cn(
        "bg-white rounded-md shadow-md relative hover:outline-2 hover:outline-primary/30 outline outline-0",
        className,
      )}
    >
      <div className="flex items-center justify-between p-2 font-medium border-b border-gray-200 shadow-sm dragHandle">
        <div className="flex items-center gap-2">
          <Badge className="gap-1 text-primary" variant="outline">
            <span>{icon}</span>
            {title}
          </Badge>
        </div>
        <div className="flex items-center gap-[-8px] opacity-20">
          {Array.from({ length: 5 }).map((_, idx) => (
            <GripIcon key={`grip-handle-${idx}-${id}`} size={16} />
          ))}
        </div>
        <button
          onClick={handleRemove}
          className="p-0.5 transition-colors border border-destructive/70 rounded-full hover:bg-destructive hover:text-destructive-foreground text-destructive/70"
        >
          <X size={12} />
          <span className="sr-only">Close</span>
        </button>
      </div>
      <div className="relative p-4 cursor-auto">
        <div className="flex flex-col gap-2">{children}</div>
        <NodeHandles handles={handles} />
      </div>
    </div>
  );
};

export default BaseNode;
